(() => {
  const experienceSelector = 'sesami-experience';
  const interfaceSelector = 'sesami-navigation, sesami-monthly-calendar';
  const interfaceContainerSelector = 'sesami-window';
  const buttonStyleAttribute = 'data-theme-button-typography';
  const interfaceStyleAttribute = 'data-theme-interface-typography';
  const lightDomStyleAttribute = 'data-theme-sesami-typography';
  const observedRoots = new WeakSet();
  const pendingComponents = new WeakSet();

  const getThemeValue = (property, fallback) => {
    const value = window
      .getComputedStyle(document.documentElement)
      .getPropertyValue(property)
      .trim();

    return value || fallback;
  };

  const getThemeTypography = () => ({
    bodyFamily: getThemeValue('--font-body-family', window.getComputedStyle(document.body).fontFamily),
    bodyStyle: getThemeValue('--font-body-style', 'normal'),
    bodyWeight: getThemeValue('--font-body-weight', '400'),
    bodyLineHeight: getThemeValue('--font-body-line-height', '1.4'),
    headingFamily: getThemeValue('--font-heading-family', window.getComputedStyle(document.body).fontFamily),
    headingStyle: getThemeValue('--font-heading-style', 'normal'),
    headingWeight: getThemeValue('--font-heading-weight', '600'),
    headingLetterSpacing: getThemeValue('--font-heading-letter-spacing', 'normal'),
    headingTextTransform: getThemeValue('--font-heading-text-transform', 'none'),
    buttonFamily: getThemeValue('--font-button-family', window.getComputedStyle(document.body).fontFamily),
    buttonStyle: getThemeValue('--font-button-style', 'normal'),
    buttonWeight: getThemeValue('--font-button-weight', '600'),
    buttonLetterSpacing: getThemeValue('--font-button-letter-spacing', 'normal'),
    buttonTextTransform: getThemeValue('--font-button-text-transform', 'none'),
    buttonSize: getThemeValue('--font-size-button', '1.4rem'),
  });
  
  const getBookingButtonLabel = (experience) => {
    const productSection = experience.closest('[data-sesami-booking-label]');
    return productSection?.dataset.sesamiBookingLabel?.trim() || '';
  };

  const applyBookingButtonLabel = (experience, shadowRoot) => {
    const label = getBookingButtonLabel(experience);
    if (!label) return;

    if (experience.getAttribute('button-label') !== label) {
      experience.setAttribute('button-label', label);
    }

    const button = shadowRoot.querySelector('.sesami-calendar');
    if (!button || button.textContent.trim() === label) return;

    const labelNode = Array.from(button.childNodes).find(
      (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
    );

    if (labelNode) {
      labelNode.textContent = label;
    } else {
      button.appendChild(document.createTextNode(label));
    }
  };

  const injectLightDomTypography = () => {
    const typography = getThemeTypography();
    const lightDomCss = `
      sesami-experience .sesami-calendar {
        font-family: ${typography.buttonFamily} !important;
        font-size: ${typography.buttonSize} !important;
        font-style: ${typography.buttonStyle} !important;
        font-weight: ${typography.buttonWeight} !important;
        letter-spacing: ${typography.buttonLetterSpacing} !important;
        text-transform: ${typography.buttonTextTransform} !important;
      }

      sesami-navigation,
      sesami-navigation *,
      sesami-monthly-calendar,
      sesami-monthly-calendar * {
        font-family: ${typography.bodyFamily} !important;
      }

      sesami-navigation h1,
      sesami-navigation h2,
      sesami-navigation h3,
      sesami-navigation h4,
      sesami-navigation h5,
      sesami-navigation h6,
      sesami-navigation [role='heading'],
      sesami-monthly-calendar h1,
      sesami-monthly-calendar h2,
      sesami-monthly-calendar h3,
      sesami-monthly-calendar h4,
      sesami-monthly-calendar h5,
      sesami-monthly-calendar h6,
      sesami-monthly-calendar [role='heading'] {
        font-family: ${typography.headingFamily} !important;
      }

      sesami-navigation button,
      sesami-navigation [role='button'],
      sesami-navigation input[type='button'],
      sesami-navigation input[type='submit'],
      sesami-monthly-calendar button,
      sesami-monthly-calendar [role='button'],
      sesami-monthly-calendar input[type='button'],
      sesami-monthly-calendar input[type='submit'] {
        font-family: ${typography.buttonFamily} !important;
      }
    `;

    let style = document.head.querySelector(`[${lightDomStyleAttribute}]`);
    if (!style) {
      style = document.createElement('style');
      style.setAttribute(lightDomStyleAttribute, '');
      document.head.appendChild(style);
    }
    if (style.textContent !== lightDomCss) style.textContent = lightDomCss;
  };

  const observeRoot = (root, reinjectStyles) => {
    if (!root || observedRoots.has(root)) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) initializeComponents(node);
        });
      });

      reinjectStyles?.();
    });

    observer.observe(root, { childList: true, subtree: true });
    observedRoots.add(root);
  };

  const injectButtonTypography = (experience) => {
    const shadowRoot = experience.shadowRoot;

    if (!shadowRoot) return false;

    const typography = getThemeTypography();
    const buttonCss = `
      .sesami-calendar {
        font-family: ${typography.buttonFamily} !important;
        font-size: ${typography.buttonSize} !important;
        font-style: ${typography.buttonStyle} !important;
        font-weight: ${typography.buttonWeight} !important;
        line-height: ${typography.bodyLineHeight} !important;
        letter-spacing: ${typography.buttonLetterSpacing} !important;
        text-transform: ${typography.buttonTextTransform} !important;
        margin-top: 1.6rem !important;
      }
        
      :host([data-sticky-sesami]) .sesami-calendar {
        margin-top: 0 !important;
      }
    `;

    let style = shadowRoot.querySelector(`[${buttonStyleAttribute}]`);

    if (!style) {
      style = document.createElement('style');
      style.setAttribute(buttonStyleAttribute, '');
      shadowRoot.appendChild(style);
    }

    if (style.textContent !== buttonCss) style.textContent = buttonCss;
    if (style !== shadowRoot.lastElementChild) shadowRoot.appendChild(style);
    applyBookingButtonLabel(experience, shadowRoot);
    experience.dataset.themeTypographyReady = 'true';

    initializeComponents(shadowRoot);
    observeRoot(shadowRoot, () => injectButtonTypography(experience));

    return true;
  };

  const injectInterfaceTypography = (component) => {
    const shadowRoot = component.shadowRoot;

    if (!shadowRoot) return false;

    const typography = getThemeTypography();
    const interfaceCss = `
      :host,
      :host * {
        font-family: ${typography.bodyFamily} !important;
      }

      :host h1,
      :host h2,
      :host h3,
      :host h4,
      :host h5,
      :host h6,
      :host [role='heading'] {
        font-family: ${typography.headingFamily} !important;
        font-style: ${typography.headingStyle} !important;
        font-weight: ${typography.headingWeight} !important;
        letter-spacing: ${typography.headingLetterSpacing} !important;
        text-transform: ${typography.headingTextTransform} !important;
      }

      :host button,
      :host [role='button'],
      :host input[type='button'],
      :host input[type='submit'] {
        font-family: ${typography.buttonFamily} !important;
        font-style: ${typography.buttonStyle} !important;
        font-weight: ${typography.buttonWeight} !important;
        letter-spacing: ${typography.buttonLetterSpacing} !important;
        text-transform: ${typography.buttonTextTransform} !important;
      }

      :host input,
      :host select,
      :host textarea {
        font-family: ${typography.bodyFamily} !important;
        font-style: ${typography.bodyStyle} !important;
        font-weight: ${typography.bodyWeight} !important;
      }
    `;

    let style = shadowRoot.querySelector(`[${interfaceStyleAttribute}]`);

    if (!style) {
      style = document.createElement('style');
      style.setAttribute(interfaceStyleAttribute, '');
      shadowRoot.appendChild(style);
    }

    if (style.textContent !== interfaceCss) style.textContent = interfaceCss;
    if (style !== shadowRoot.lastElementChild) shadowRoot.appendChild(style);
    component.dataset.themeTypographyReady = 'true';

    initializeComponents(shadowRoot);
    observeRoot(shadowRoot, () => injectInterfaceTypography(component));

    return true;
  };

  const initializeInterfaceContainer = (container) => {
    const shadowRoot = container.shadowRoot;

    if (!shadowRoot) return false;

    initializeComponents(shadowRoot);
    observeRoot(shadowRoot);

    return true;
  };

  const initializeComponent = (component, injectStyles) => {
    if (injectStyles(component) || pendingComponents.has(component)) return;

    pendingComponents.add(component);
    let attemptCount = 0;
    const waitForShadowRoot = window.setInterval(() => {
      attemptCount += 1;

      if (injectStyles(component) || attemptCount >= 100) {
        window.clearInterval(waitForShadowRoot);
        pendingComponents.delete(component);
      }
    }, 100);
  };

  const initializeComponents = (root = document) => {
    if (root.matches?.(experienceSelector)) {
      initializeComponent(root, injectButtonTypography);
    }
    if (root.matches?.(interfaceSelector)) {
      initializeComponent(root, injectInterfaceTypography);
    }
    if (root.matches?.(interfaceContainerSelector)) {
      initializeComponent(root, initializeInterfaceContainer);
    }

    root.querySelectorAll?.(experienceSelector).forEach((experience) => {
      initializeComponent(experience, injectButtonTypography);
    });
    root.querySelectorAll?.(interfaceSelector).forEach((component) => {
      initializeComponent(component, injectInterfaceTypography);
    });
    root.querySelectorAll?.(interfaceContainerSelector).forEach((container) => {
      initializeComponent(container, initializeInterfaceContainer);
    });
  };

  injectLightDomTypography();
  initializeComponents();
  observeRoot(document.documentElement);
  document.addEventListener('shopify:section:load', (event) => {
    injectLightDomTypography();
    initializeComponents(event.target);
  });
})();
